import { useEffect, useState, useRef } from "react";
import axios from "axios";
import { toast } from "react-toastify";
import { confirmAlert } from "react-confirm-alert";
import "react-confirm-alert/src/react-confirm-alert.css";
import { FaRegTrashAlt, FaRegEdit, FaStar } from "react-icons/fa";
import { MdAdd, MdClose, MdCheck, MdKeyboardArrowUp, MdKeyboardArrowDown } from "react-icons/md";
import { ImSpinner2 } from "react-icons/im";
import { FiMessageCircle } from "react-icons/fi";
import PanelSkeleton from "../PanelSkeleton";
import store from "../../redux/store";
import styles from "../../styles/categoriesPanel.module.css";
import tstStyles from "../../styles/testimonialsPanel.module.css";

const authConfig = (extra = {}) => {
  const token = store.getState().auth?.token;
  return {
    withCredentials: true,
    ...extra,
    headers: { ...(token ? { Authorization: `Bearer ${token}` } : {}), ...extra.headers },
  };
};

const API = `${process.env.REACT_APP_BACKEND_URL}/api/testimonials`;
// The home page chat has room for exactly four customer messages
const MAX_TESTIMONIALS = 4;

const DEFAULT_FORM = { name: "", text: "", stars: 5 };

const TestimonialsPanel = () => {
  const [items, setItems]           = useState([]);
  const [loading, setLoading]       = useState(true);
  const [saving, setSaving]         = useState(false);
  const [deleting, setDeleting]     = useState(false);
  const [reordering, setReordering] = useState(false);
  const [editId, setEditId]         = useState(null);
  const [form, setForm]             = useState(DEFAULT_FORM);
  const [showForm, setShowForm]     = useState(false);
  const nameRef = useRef(null);

  const isFull = items.length >= MAX_TESTIMONIALS;

  const fetchItems = async () => {
    try {
      const res = await axios.get(`${API}/dashboardFetch`, authConfig());
      setItems(res.data?.data ?? []);
    } catch {
      toast.error(<span className="toast">فشل تحميل آراء العملاء</span>);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchItems(); }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const openAdd = () => {
    setEditId(null);
    setForm(DEFAULT_FORM);
    setShowForm(true);
    setTimeout(() => nameRef.current?.focus(), 80);
  };

  const openEdit = (item) => {
    setEditId(item.id);
    setForm({ name: item.name, text: item.text, stars: item.stars });
    setShowForm(true);
    setTimeout(() => nameRef.current?.focus(), 80);
  };

  const closeForm = () => {
    setShowForm(false);
    setEditId(null);
    setForm(DEFAULT_FORM);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.name.trim() || !form.text.trim()) return;
    setSaving(true);
    try {
      const body = { name: form.name.trim(), text: form.text.trim(), stars: form.stars };
      if (editId) {
        await axios.put(`${API}/${editId}`, body, authConfig());
        toast.success(<span className="toast">تم تعديل الرأي</span>);
      } else {
        await axios.post(API, body, authConfig());
        toast.success(<span className="toast">تم إضافة الرأي</span>);
      }
      closeForm();
      fetchItems();
    } catch (err) {
      const limitHit = String(err?.response?.data?.message ?? "").includes("Maximum");
      toast.error(
        <span className="toast">
          {limitHit ? `الحد الأقصى ${MAX_TESTIMONIALS} آراء فقط` : "فشلت العملية. حاول مرة أخرى"}
        </span>
      );
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = (item) => {
    confirmAlert({
      message: `حذف رأي "${item.name}" ؟`,
      buttons: [
        {
          label: "نعم",
          onClick: async () => {
            setDeleting(true);
            try {
              await axios.delete(`${API}/${item.id}`, authConfig());
              toast.success(<span className="toast">تم حذف الرأي</span>);
              if (editId === item.id) closeForm();
              fetchItems();
            } catch {
              toast.error(<span className="toast">فشل الحذف. حاول مرة أخرى</span>);
            } finally {
              setDeleting(false);
            }
          },
        },
        { label: "لا" },
      ],
    });
  };

  const move = async (index, direction) => {
    const next = [...items];
    const swapIdx = direction === "up" ? index - 1 : index + 1;
    if (swapIdx < 0 || swapIdx >= next.length) return;

    [next[index], next[swapIdx]] = [next[swapIdx], next[index]];
    setItems(next);

    setReordering(true);
    try {
      await axios.put(
        `${API}/reorder/batch`,
        { items: next.map((item, i) => ({ id: item.id, sortOrder: i })) },
        authConfig()
      );
    } catch {
      toast.error(<span className="toast">فشل حفظ الترتيب. حاول مرة أخرى</span>);
      fetchItems();
    } finally {
      setReordering(false);
    }
  };

  return (
    <div className={styles.panel}>

      {/* ── Header ── */}
      <div className={styles.panelHead}>
        <span className={styles.panelTitle}>
          <FiMessageCircle className={styles.panelTitleIcon} />
          آراء العملاء ({items.length}/{MAX_TESTIMONIALS})
        </span>
        <button className={styles.addBtn} onClick={openAdd} disabled={showForm || isFull || loading}>
          <MdAdd /> إضافة رأي
        </button>
      </div>

      {/* ── Add / Edit Form ── */}
      {showForm && (
        <form className={styles.form} onSubmit={handleSubmit}>
          <div className={styles.formHead}>
            <span className={styles.formTitle}>{editId ? "تعديل الرأي" : "رأي جديد"}</span>
            <button type="button" className={styles.formClose} onClick={closeForm}>
              <MdClose />
            </button>
          </div>

          <div className={styles.formRow}>
            <label className={styles.formLabel}>اسم العميل *</label>
            <input
              ref={nameRef}
              className={styles.formInput}
              placeholder="مثال: أم محمد"
              maxLength={50}
              value={form.name}
              onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))}
              required
            />
          </div>

          <div className={styles.formRow}>
            <label className={styles.formLabel}>نص الرسالة *</label>
            <textarea
              className={`${styles.formInput} ${tstStyles.textarea}`}
              placeholder="مثال: اللوحة وصلت رائعة وفاقت توقعاتي!"
              maxLength={500}
              rows={3}
              value={form.text}
              onChange={(e) => setForm((f) => ({ ...f, text: e.target.value }))}
              required
            />
          </div>

          <div className={styles.formRow}>
            <label className={styles.formLabel}>التقييم</label>
            <div className={tstStyles.stars}>
              {[1, 2, 3, 4, 5].map((n) => (
                <button
                  type="button"
                  key={n}
                  className={`${tstStyles.star} ${n <= form.stars ? tstStyles.starOn : ""}`}
                  onClick={() => setForm((f) => ({ ...f, stars: n }))}
                  aria-label={`${n} نجوم`}
                >
                  <FaStar />
                </button>
              ))}
            </div>
          </div>

          <div className={styles.formActions}>
            <button type="submit" className={styles.saveBtn} disabled={saving}>
              {saving ? <ImSpinner2 className="smallspinner" /> : <><MdCheck /> حفظ</>}
            </button>
            <button type="button" className={styles.cancelFormBtn} onClick={closeForm}>
              إلغاء
            </button>
          </div>
        </form>
      )}

      {/* ── List ── */}
      {loading ? (
        <PanelSkeleton count={3} />
      ) : items.length === 0 ? (
        <p className="warn">لا توجد آراء بعد. تظهر الآراء الافتراضية في الصفحة الرئيسية حتى تضيف أول رأي.</p>
      ) : (
        <div className={styles.list}>
          {items.map((item, i) => (
            <div key={item.id} className={styles.row} style={{ animationDelay: `${i * 0.04}s` }}>

              <div className={styles.orderBtns}>
                <button
                  className={styles.orderBtn}
                  onClick={() => move(i, "up")}
                  disabled={i === 0 || reordering || deleting}
                  title="تحريك لأعلى"
                >
                  <MdKeyboardArrowUp />
                </button>
                <button
                  className={styles.orderBtn}
                  onClick={() => move(i, "down")}
                  disabled={i === items.length - 1 || reordering || deleting}
                  title="تحريك لأسفل"
                >
                  <MdKeyboardArrowDown />
                </button>
              </div>

              <div className={tstStyles.info}>
                <span className={styles.catName}>
                  {item.name} <span className={tstStyles.rowStars}>{"★".repeat(item.stars)}</span>
                </span>
                <span className={tstStyles.text}>{item.text}</span>
              </div>

              <span className={styles.orderBadge}>{i + 1}</span>

              <div className={styles.actions}>
                <button
                  className={styles.editBtn}
                  onClick={() => openEdit(item)}
                  disabled={deleting}
                  title="تعديل"
                >
                  <FaRegEdit />
                </button>
                <button
                  className={styles.deleteBtn}
                  onClick={() => !deleting && handleDelete(item)}
                  disabled={deleting}
                  title="حذف"
                >
                  {deleting ? <ImSpinner2 className="smallspinner" /> : <FaRegTrashAlt />}
                </button>
              </div>
            </div>
          ))}
        </div>
      )}

      <p className={styles.hint}>
        تظهر هذه الآراء كرسائل العملاء في قسم "عملاؤنا يتحدثون عنّا" بالصفحة الرئيسية (بحد أقصى {MAX_TESTIMONIALS}).
        ردود المتجر تبقى كما هي.
      </p>
    </div>
  );
};

export default TestimonialsPanel;
